// queueSync.ts — synkronoi mutation queue:ssa olevat kutsut backendiin.
//
// Käynnistyy automaattisesti kun:
// - 'online'-event laukeaa (verkko palaa)
// - Sovellus käynnistyy ja queueessa on jo aiempia mutaatioita (esim.
//   selaimen sulkemisen yli säilyneet)
//
// FIFO-järjestys (createdAt asc), max 3 retryä per item. Onnistuneet
// poistetaan, epäonnistuneet retryllä jää queueseen lokin kanssa.

import { getAll, remove, update, type QueuedMutation } from './mutationQueue';

// 10.7.2026 (bugikorjaus, Aleksanterin jumiutunut "Synkronoidaan 2 muutosta…"):
// - Vanha MAX_RETRIES=3 + skip-logiikka jätti epäonnistuneet mutaatiot jonoon
//   IKUISESTI (silmukka ohitti ne mutta remaining laski ne mukaan → banneri jäi
//   pysyvästi näkyviin eikä mikään enää yrittänyt synkata niitä).
// - 401 (vanhentunut tunnus yön yli) poltti retry-yritykset heti aamulla.
// Nyt: jokainen ajo yrittää kaikkia; 401 keskeyttää polttamatta yrityksiä
// (odottaa kirjautumista); transientit saavat 20 yritystä ja sen jälkeen
// mutaatio HYLÄTÄÄN näkyvästi (dropped-event → banneri kertoo käyttäjälle).
const MAX_RETRIES = 20;
let isSyncing = false;

interface SyncResult {
  succeeded: number;
  failed: number;
  remaining: number;
  dropped: number;
  authBlocked: boolean;
}

type SyncOutcome = 'ok' | 'permanent' | 'auth' | 'transient';

const NOOP_RESULT: SyncResult = { succeeded: 0, failed: 0, remaining: 0, dropped: 0, authBlocked: false };

export async function syncQueue(getAccessToken: () => string | null): Promise<SyncResult> {
  // P2 (28.7.2026): isSyncing suojaa vain SAMAN välilehden — kaksi välilehteä
  // (kummallakin oma moduulitila) saattoi replayata saman jonon yhtä aikaa →
  // sama POST lähti kahdesti (esim. tuplapoikkeama). Web Locks -lukko on
  // selainkohtainen: vain yksi välilehti synkkaa kerrallaan, muut ohittavat
  // (jono on jaettu IndexedDB:ssä, lukon haltija hoitaa sen kokonaan).
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('savroc-queue-sync', { ifAvailable: true }, async lock => {
      if (!lock) return NOOP_RESULT;
      return doSyncQueue(getAccessToken);
    });
  }
  return doSyncQueue(getAccessToken);
}

async function doSyncQueue(getAccessToken: () => string | null): Promise<SyncResult> {
  if (isSyncing) return NOOP_RESULT;
  isSyncing = true;

  try {
    const items = await getAll();
    let succeeded = 0;
    let failed = 0;
    let dropped = 0;
    let authBlocked = false;

    for (const item of items) {
      const outcome = await syncOne(item, getAccessToken);
      if (outcome === 'ok') {
        if (item.id !== undefined) await remove(item.id);
        succeeded++;
      } else if (outcome === 'permanent') {
        if (item.id !== undefined) await remove(item.id);
        dropped++;
      } else if (outcome === 'auth') {
        // Kirjautuminen vanhentunut — ÄLÄ kuluta yrityksiä, odota tuoretta tunnusta
        authBlocked = true;
        failed += items.length - succeeded - dropped;
        break;
      } else {
        const retries = item.retries + 1;
        if (retries >= MAX_RETRIES) {
          if (item.id !== undefined) await remove(item.id);
          dropped++;
           
          console.warn(`[queueSync] Mutaatio ${item.method} ${item.url} hylätty ${MAX_RETRIES} yrityksen jälkeen`);
        } else {
          if (item.id !== undefined) {
            await update(item.id, { retries, lastError: 'Synkronointi epäonnistui' });
          }
          failed++;
        }
      }
    }

    const remaining = (await getAll()).length;
    window.dispatchEvent(new CustomEvent('queue:synced', { detail: { succeeded, failed, remaining, dropped, authBlocked } }));
    return { succeeded, failed, remaining, dropped, authBlocked };
  } finally {
    isSyncing = false;
  }
}

async function syncOne(item: QueuedMutation, getAccessToken: () => string | null): Promise<SyncOutcome> {
  try {
    const headers: Record<string, string> = { ...item.headers };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (item.body && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(item.url, {
      method: item.method,
      headers,
      credentials: 'include',
      body: item.body,
    });

    if (res.ok) return 'ok';
    if (res.status === 401) return 'auth';
    // 4xx-virheet (paitsi 401/408/429) ovat permanenteja — ei kannata retrytä
    // (esim. 400 = invalid body, 403 = ei oikeuksia, 404 = entity poistettu).
    if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
       
      console.warn(`[queueSync] Pysyvä virhe ${res.status} mutaatiolle ${item.method} ${item.url} — hylätään`);
      return 'permanent';
    }
    return 'transient';
  } catch {
    // Network error → retryyn
    return 'transient';
  }
}

// ── Auto-trigger ─────────────────────────────────────────────

let registered = false;

/**
 * Rekisteröi automaattisen synkronoinnin:
 * - Käynnistyy heti (jos queueessa on aiempia mutaatioita)
 * - Triggeröityy kun 'online'-event laukeaa
 *
 * Kutsu kerran sovelluksen käynnistyessä (esim. main.tsx tai App.tsx mountissa).
 */
export function registerAutoSync(getAccessToken: () => string | null): void {
  if (registered) return;
  registered = true;

  const trigger = () => {
    if (navigator.onLine) {
      void syncQueue(getAccessToken);
    }
  };

  window.addEventListener('online', trigger);
  // 10.7.2026: myös sovellukseen palatessa + varmistusajo minuutin välein kun
  // jonossa on jotain — aiemmin epäonnistuneen käynnistyssynkan jälkeen MIKÄÄN
  // ei enää yrittänyt uudelleen ennen seuraavaa online-eventiä.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') trigger();
  });
  setInterval(() => {
    void getAll().then(items => { if (items.length > 0) trigger(); });
  }, 60_000);
  // Heti käynnistyksessä — jos queueessa on yli yön jääneitä mutaatioita
  if (navigator.onLine) {
    void syncQueue(getAccessToken);
  }
}
